import React from 'react';
import Framework from "@beardeddevops/react.framework";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import {Badge, Progress, Button, Modal, ModalHeader, ModalBody} from 'reactstrap';
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import {PieChart} from 'react-chartkick';
import 'chart.js';
import MrpLocationControl from "components/Parts/MrpLocationControl";
import {applyMrpLocationToRequest, getMrpLocationSignature, syncMrpActiveLocation} from "utils/mrpLocation";

const LOCATION_NOTE = 'ERP reads and writes follow the active location shown here. Changing it updates data across ERP screens.';

const breadcrumbsStatic = [
	{
		name: 'Dashboard',
		url: '/',
		icon: 'dashboard',
	},
	{
		name: 'ERP',
		url: '/erp',
		icon: 'industry',
	},
];

class MrpDashboard extends Framework.Components.View {
	constructor(props) {
		super(props);
		this.state = {
			...this.state,
			loading: true,
			now: Date.now(),
			jobs: [],
			stations: [],
			parts: [],
			models: [],
			lots: [],
			reportsLoading: false,
			reportsError: '',
			reportDays: 30,
			reports: {
				financial: [],
				parts_inventory: [],
				guns_built: [],
				labor_time: [],
				scrap: [],
			},
			reportSummary: {},
			logsModalOpen: false,
			logsLoading: false,
			logsError: '',
			logsEntries: [],
			lowStockModalOpen: false,
			lowStockFilterText: '',
			logsJobId: null,
			logsJobName: '',
			logsFilterType: 'all',
			logsFilterText: '',
			logsFilterFrom: '',
			logsFilterTo: '',
		};
		this.logsRequestToken = 0;
	}

	componentDidMount() {
		if (super.componentDidMount) {
			super.componentDidMount();
		}
		syncMrpActiveLocation(this.props.rootState);
		this.loadDashboardData();
	}

	componentDidUpdate(prevProps, prevState) {
		if (getMrpLocationSignature(prevProps?.rootState) !== getMrpLocationSignature(this.props?.rootState)) {
			this.loadDashboardData();
		}
		if (prevState?.jobs !== this.state.jobs) {
			this.manageTick();
		}
	}

	componentWillUnmount() {
		this.stopTick();
	}

	getAccountParent = () => {
		return this.props?.rootState?.account_number || '9999-9999999';
	}

	getEmptyReports = () => ({
		financial: [],
		parts_inventory: [],
		guns_built: [],
		labor_time: [],
		scrap: [],
	})

	normalizeReportRows = (rows) => {
		return Array.isArray(rows) ? rows : [];
	}

	normalizeReportsPayload = (response) => {
		const payload = (response && typeof response === 'object' && response.reports) ? response.reports : response;
		return {
			financial: this.normalizeReportRows(payload?.financial),
			parts_inventory: this.normalizeReportRows(payload?.parts_inventory),
			guns_built: this.normalizeReportRows(payload?.guns_built),
			labor_time: this.normalizeReportRows(payload?.labor_time),
			scrap: this.normalizeReportRows(payload?.scrap),
		};
	}

	loadDashboardData = async () => {
		this.setState({loading: true, reportsLoading: true, reportsError: ''});
		try {
			const [jobs, stations, parts, models, lots, reportPayload] = await Promise.all([
				this.loadJobs(),
				this.loadCollection(Types.Mrp_Stations._name),
				this.loadCollection(Types.Mrp_Parts._name),
				this.loadCollection(Types.Mrp_Models._name),
				this.loadCollection(Types.Mrp_Lots._name),
				this.loadReportsData(this.state.reportDays),
			]);
			this.setState({
				jobs,
				stations,
				parts,
				models,
				lots,
				reports: reportPayload.reports,
				reportSummary: reportPayload.summary,
				reportsError: reportPayload.error || '',
				reportsLoading: false,
				loading: false
			}, this.manageTick);
		} catch (error) {
			console.error('ERP Dashboard load error:', error);
			this.setState({
				loading: false,
				reportsLoading: false,
				reportsError: 'Failed to load dashboard reports.'
			}, this.manageTick);
		}
	}

	loadJobs = async () => {
		try {
			const model = new API.BuildClass(Types.Mrp_Jobs._name, 'GET_NORMALIZED');
			model.parent = this.getAccountParent();
			applyMrpLocationToRequest(model, this.props.rootState);
			const result = await model.submit(null, 'GET_NORMALIZED');
			const rawItems = result?.items ?? result?.item ?? result?.data ?? result ?? [];
			let items = Array.isArray(rawItems) ? rawItems : (rawItems ? [rawItems] : []);
			if (items.length === 0 && rawItems && typeof rawItems === 'object') {
				const hasIdentity = rawItems.pkey || rawItems.id;
				items = hasIdentity ? [rawItems] : [];
			}
			return items.map(this.normalizeJob);
		} catch (error) {
			return [];
		}
	}

	loadCollection = async (typeName) => {
		try {
			let model = new API.BuildClass(typeName, API.RequestType.READ_ALL);
			model.parent = this.getAccountParent();
			applyMrpLocationToRequest(model, this.props.rootState);
			const result = await model.submit(null, API.RequestType.READ_ALL);
			const raw = result?.items ?? result?.data ?? result ?? [];
			return Array.isArray(raw) ? raw : (raw ? [raw] : []);
		} catch (error) {
			return [];
		}
	}

	loadReportsData = async (days = 30) => {
		const safeDays = Number.isFinite(Number(days)) && Number(days) > 0 ? Number(days) : 30;
		const fallback = {
			reports: this.getEmptyReports(),
			summary: {},
			error: ''
		};
		try {
			const model = new API.BuildClass(Types.Mrp_Jobs._name, 'GET_REPORTS');
			model.parent = this.getAccountParent();
			model.object.days = safeDays;
			model.object.limit = 100;
			applyMrpLocationToRequest(model, this.props.rootState);
			const response = await model.submit(null, 'GET_REPORTS');
			return {
				reports: this.normalizeReportsPayload(response),
				summary: (response && typeof response === 'object' && response.summary && typeof response.summary === 'object') ? response.summary : {},
				error: ''
			};
		} catch (error) {
			return {
				...fallback,
				error: 'Failed to load reports.'
			};
		}
	}

	handleReportWindowChange = async (days) => {
		this.setState({reportDays: days, reportsLoading: true, reportsError: ''});
		const reportPayload = await this.loadReportsData(days);
		this.setState({
			reports: reportPayload.reports,
			reportSummary: reportPayload.summary,
			reportsError: reportPayload.error || '',
			reportsLoading: false
		});
	}

	handleRefreshReports = async () => {
		this.setState({reportsLoading: true, reportsError: ''});
		const reportPayload = await this.loadReportsData(this.state.reportDays);
		this.setState({
			reports: reportPayload.reports,
			reportSummary: reportPayload.summary,
			reportsError: reportPayload.error || '',
			reportsLoading: false
		});
	}

	// --- Workflow / Status helpers (mirrors MrpJobTracker patterns) ---

	parseWorkflow = (workflow) => {
		if (!workflow) return null;
		if (typeof workflow === 'string') {
			try {
				return JSON.parse(workflow);
			} catch (e) {
				return null;
			}
		}
		return typeof workflow === 'object' ? workflow : null;
	}

	normalizeJob = (job) => {
		const workflow = this.parseWorkflow(job.workflow);
		const id = job.pkey || job.id;
		const status = job.status || workflow?.job?.status || 'in_progress';
		const name = job.name || workflow?.job?.name || `Job ${id}`;
		const modelName = workflow?.model?.name || workflow?.job?.model_name || '';
		const quantity = workflow?.job?.quantity ?? workflow?.quantity ?? job.quantity ?? job.job_quantity ?? job.qty ?? '';
		const lotId = job.lot || workflow?.job?.lot_id || '';
		return {
			...job,
			id,
			name,
			status,
			workflow,
			modelName,
			quantity,
			lotId,
		};
	}

	getJobStatus = (job) => {
		const workflow = this.parseWorkflow(job.workflow);
		return job.status || workflow?.job?.status || 'in_progress';
	}

	isJobActive = (status) => {
		const normalized = `${status || ''}`.toLowerCase();
		return !['complete', 'completed', 'done', 'closed', 'archived'].includes(normalized);
	}

	formatStatusLabel = (status) => {
		if (!status) return 'In Progress';
		return `${status}`.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
	}

	normalizeOrderUrl = (urlValue) => {
		const trimmed = `${urlValue || ''}`.trim();
		if (!trimmed) {
			return '';
		}
		if (/^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(trimmed)) {
			return trimmed;
		}
		return `https://${trimmed}`;
	}

	getStatusColor = (status) => {
		const normalized = `${status || ''}`.toLowerCase();
		if (['complete', 'completed', 'done'].includes(normalized)) return 'success';
		if (['blocked', 'paused', 'on hold'].includes(normalized)) return 'warning';
		if (['error', 'failed', 'canceled'].includes(normalized)) return 'danger';
		if (['in progress', 'in_progress', 'active', 'started'].includes(normalized)) return 'primary';
		return 'secondary';
	}

	getJobProgress = (job) => {
		const workflow = this.parseWorkflow(job.workflow);
		if (!workflow || !workflow.stations) return {percent: 0, completedStations: 0, totalStations: 0};
		const stations = workflow.stations;
		const totalStations = stations.length;
		const completedStations = stations.filter(s => {
			const normalized = `${s.status || ''}`.toLowerCase();
			return ['complete', 'completed', 'done'].includes(normalized);
		}).length;
		const percent = totalStations > 0 ? Math.round((completedStations / totalStations) * 100) : 0;
		return {percent, completedStations, totalStations};
	}

	isEntryArchived = (entry) => {
		if (!entry) {
			return false;
		}
		const value = entry.archived;
		return value === true || value === 1 || value === '1';
	}

	getEntryType = (entry) => {
		const normalizedType = `${entry?.entry_type || ''}`.toLowerCase();
		if (!normalizedType) {
			return 'labor';
		}
		return normalizedType;
	}

	getEntrySeconds = (entry) => {
		if (!entry) {
			return 0;
		}
		if (entry.started_at && !entry.ended_at) {
			const started = Date.parse(entry.started_at);
			const now = this.state.now || Date.now();
			if (!Number.isNaN(started)) {
				return Math.max(0, Math.round((now - started) / 1000));
			}
		}
		if (entry.duration_minutes || entry.duration_minutes === 0) {
			return Math.max(0, Math.round((Number(entry.duration_minutes) || 0) * 60));
		}
		if (entry.minutes || entry.minutes === 0) {
			return Math.max(0, Math.round((Number(entry.minutes) || 0) * 60));
		}
		if (entry.started_at && entry.ended_at) {
			const started = Date.parse(entry.started_at);
			const ended = Date.parse(entry.ended_at);
			if (!Number.isNaN(started) && !Number.isNaN(ended)) {
				return Math.max(0, Math.round((ended - started) / 1000));
			}
		}
		return 0;
	}

	hasActiveEntries = (stations) => {
		return this.collectJobTimeEntries(stations).some((entry) => !entry?.ended_at);
	}

	hasAnyActiveTimers = () => {
		return this.state.jobs.some((job) => this.hasActiveEntries(this.parseWorkflow(job.workflow)?.stations || []));
	}

	manageTick = () => {
		if (this.hasAnyActiveTimers()) {
			this.startTick();
			return;
		}
		this.stopTick();
	}

	startTick = () => {
		if (this._tickInterval) {
			return;
		}
		this._tickInterval = setInterval(() => {
			this.setState({now: Date.now()});
		}, 1000);
	}

	stopTick = () => {
		if (this._tickInterval) {
			clearInterval(this._tickInterval);
			this._tickInterval = null;
		}
	}

	collectJobTimeEntries = (stations) => {
		const dedupedEntries = new Map();
		const addEntry = (entry, fallbackKey) => {
			if (!entry || this.isEntryArchived(entry)) {
				return;
			}
			const hasEntryId = entry.time_entry_id !== null
				&& entry.time_entry_id !== undefined
				&& `${entry.time_entry_id}` !== '';
			const entryKey = hasEntryId ? `id:${entry.time_entry_id}` : `fallback:${fallbackKey}`;
			if (hasEntryId && dedupedEntries.has(entryKey)) {
				return;
			}
			dedupedEntries.set(entryKey, entry);
		};

		(stations || []).forEach((station, stationIndex) => {
			(station?.time_entries || []).forEach((entry, entryIndex) => {
				addEntry(entry, `station:${stationIndex}:${entryIndex}`);
			});
			(station?.parts || []).forEach((part, partIndex) => {
				(part?.time_entries || []).forEach((entry, entryIndex) => {
					addEntry(entry, `part:${stationIndex}:${partIndex}:${entryIndex}`);
				});
			});
		});

		return Array.from(dedupedEntries.values());
	}

	getJobTimeTotals = (job) => {
		const workflow = this.parseWorkflow(job.workflow);
		const entries = this.collectJobTimeEntries(workflow?.stations || []);
		let laborSeconds = 0;
		let machineSeconds = 0;
		let laborActive = false;
		let machineActive = false;

		entries.forEach((entry) => {
			const entryType = this.getEntryType(entry);
			const seconds = this.getEntrySeconds(entry);
			const isActive = !entry.ended_at;
			if (entryType === 'machine') {
				machineSeconds += seconds;
				if (isActive) {
					machineActive = true;
				}
				return;
			}
			laborSeconds += seconds;
			if (isActive) {
				laborActive = true;
			}
		});

		return {
			laborSeconds,
			machineSeconds,
			totalSeconds: laborSeconds + machineSeconds,
			laborActive,
			machineActive,
		};
	}

	formatElapsed = (totalSeconds) => {
		const seconds = Math.max(0, Math.round(totalSeconds || 0));
		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);
		const remainderSeconds = seconds % 60;
		const pad = (value) => String(value).padStart(2, '0');
		if (hours > 0) {
			return `${hours}:${pad(minutes)}:${pad(remainderSeconds)}`;
		}
		return `${minutes}:${pad(remainderSeconds)}`;
	}

	renderJobTimerMetric = (label, value, isRunning, color) => {
		return (
			<div key={label} style={styles.jobTimerMetric}>
				<span style={styles.jobTimerMetricLabel}>{label}</span>
				<span style={styles.jobTimerMetricValue}>{value}</span>
				{isRunning && (
					<Badge color={color} pill style={styles.jobTimerBadge}>
						Running
					</Badge>
				)}
			</div>
		);
	}

	getLowStockParts = () => {
		return this.state.parts.filter(part => {
			const qty = Number(part.quantity) || 0;
			const alert = Number(part.soft_alert ?? part.alert_quantity) || 0;
			return alert > 0 && qty <= alert;
		});
	}

	getLowStockSeverityRatio = (part) => {
		const qty = Number(part.quantity) || 0;
		const alert = Number(part.soft_alert ?? part.alert_quantity) || 0;
		if (alert <= 0) {
			return 1;
		}
		return qty / alert;
	}

	getFilteredLowStockParts = () => {
		const search = `${this.state.lowStockFilterText || ''}`.trim().toLowerCase();
		return this.getLowStockParts()
			.filter((part) => {
				if (!search) {
					return true;
				}
				const haystack = [
					part.name,
					part.sku,
					part.upc,
					this.getStationName(part.station),
				]
					.map((value) => `${value || ''}`.toLowerCase())
					.join(' ');
				return haystack.includes(search);
			})
			.sort((a, b) => {
				const ratioDiff = this.getLowStockSeverityRatio(a) - this.getLowStockSeverityRatio(b);
				if (ratioDiff !== 0) {
					return ratioDiff;
				}
				const qtyDiff = (Number(a.quantity) || 0) - (Number(b.quantity) || 0);
				if (qtyDiff !== 0) {
					return qtyDiff;
				}
				const alertDiff = (Number(b.soft_alert ?? b.alert_quantity) || 0) - (Number(a.soft_alert ?? a.alert_quantity) || 0);
				if (alertDiff !== 0) {
					return alertDiff;
				}
				return `${a.name || ''}`.localeCompare(`${b.name || ''}`);
			});
	}

	handleOpenLowStockModal = () => {
		if (!this.getLowStockParts().length) {
			return;
		}
		this.setState({lowStockModalOpen: true, lowStockFilterText: ''});
	}

	handleCloseLowStockModal = () => {
		this.setState({lowStockModalOpen: false, lowStockFilterText: ''});
	}

	getStationName = (stationPkey) => {
		const station = this.state.stations.find(s => `${s.pkey}` === `${stationPkey}`);
		return station ? station.name : '-';
	}

	getProgressColor = (ratio) => {
		if (ratio < 0.25) return 'danger';
		if (ratio < 0.75) return 'warning';
		return 'success';
	}

	formatDate = (value) => {
		if (!value) return '-';
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return value;
		return date.toLocaleDateString();
	}

	formatDateTime = (value) => {
		if (!value) return '-';
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return value;
		return date.toLocaleString();
	}

	formatNumber = (value, maximumFractionDigits = 2) => {
		const numeric = Number(value);
		if (!Number.isFinite(numeric)) return '-';
		return numeric.toLocaleString(undefined, {maximumFractionDigits});
	}

	formatCurrency = (value) => {
		const numeric = Number(value);
		if (!Number.isFinite(numeric)) return '-';
		return numeric.toLocaleString(undefined, {
			style: 'currency',
			currency: 'USD',
			maximumFractionDigits: 2
		});
	}

	formatLogTimestamp = (value) => {
		if (!value) return '-';
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return value;
		return date.toLocaleString();
	}

	normalizeLogTerminology = (text) => {
		const value = `${text || ''}`;
		return value
			.replace(/\b[Pp]arts\b/g, (match) => (match[0] === 'P' ? 'Items' : 'items'))
			.replace(/\b[Pp]art\b/g, (match) => (match[0] === 'P' ? 'Item' : 'item'));
	}

	formatLogDescription = (entry) => {
		const eventType = `${entry?.event_type || ''}`.toLowerCase();
		const metadata = (entry && typeof entry === 'object' && entry.metadata && typeof entry.metadata === 'object')
			? entry.metadata
			: {};
		if (eventType === 'quantity') {
			const itemName = `${metadata?.item_name || metadata?.part_name || ''}`.trim();
			const delta = Number(metadata?.delta);
			const from = Number(metadata?.from);
			const to = Number(metadata?.to);
			const hasDelta = Number.isFinite(delta);
			const hasFromTo = Number.isFinite(from) && Number.isFinite(to);
			const itemLabel = itemName ? `Item ${itemName}` : 'Item';
			if (hasDelta && hasFromTo) {
				const direction = delta >= 0 ? 'increased' : 'decreased';
				return `${itemLabel} quantity ${direction} by ${this.formatNumber(Math.abs(delta), 4)} (${this.formatNumber(from, 4)} -> ${this.formatNumber(to, 4)}).`;
			}
		}
		if (entry?.description) return this.normalizeLogTerminology(entry.description);
		const action = entry?.action ? `${entry.action}`.replace(/_/g, ' ') : 'updated';
		const eventLabel = entry?.event_type ? `${entry.event_type}`.replace(/_/g, ' ') : 'event';
		return this.normalizeLogTerminology(`${eventLabel} ${action}`.replace(/\b\w/g, c => c.toUpperCase()));
	}

	getLogActorName = (entry) => {
		const direct = `${entry?.actor_name || ''}`.trim();
		if (direct) return direct;
		const employee = `${entry?.employee_name || ''}`.trim();
		if (employee) return employee;
		const metaActor = `${entry?.metadata?.actor_name || ''}`.trim();
		if (metaActor) return metaActor;
		const metaEmployee = `${entry?.metadata?.employee_name || ''}`.trim();
		if (metaEmployee) return metaEmployee;
		return '';
	}

	getFilteredLogs = () => {
		const {logsEntries, logsFilterType, logsFilterText, logsFilterFrom, logsFilterTo, logsJobId} = this.state;
		const search = `${logsFilterText || ''}`.trim().toLowerCase();
		return logsEntries.filter((entry) => {
			if (logsJobId && `${entry?.job ?? ''}` !== `${logsJobId}`) {
				return false;
			}
			const entryType = `${entry?.event_type || ''}`.toLowerCase();
			if (logsFilterType !== 'all' && entryType !== logsFilterType) {
				return false;
			}
			const entryTime = entry?.created ? new Date(entry.created) : null;
			if (logsFilterFrom && entryTime && !Number.isNaN(entryTime.getTime())) {
				const fromDate = new Date(`${logsFilterFrom}T00:00:00`);
				if (entryTime < fromDate) {
					return false;
				}
			}
			if (logsFilterTo && entryTime && !Number.isNaN(entryTime.getTime())) {
				const toDate = new Date(`${logsFilterTo}T23:59:59.999`);
				if (entryTime > toDate) {
					return false;
				}
			}
			if (!search) {
				return true;
			}
			const desc = `${this.formatLogDescription(entry)}`.toLowerCase();
			const action = `${entry?.action || ''}`.toLowerCase();
			const type = `${entry?.event_type || ''}`.toLowerCase();
			const creator = `${entry?.creator || ''}`.toLowerCase();
			const actor = `${this.getLogActorName(entry)}`.toLowerCase();
			return desc.includes(search) || action.includes(search) || type.includes(search) || creator.includes(search) || actor.includes(search);
		});
	}

	getLogTypeOptions = () => {
		const {logsEntries} = this.state;
		const unique = Array.from(new Set(logsEntries.map((entry) => `${entry?.event_type || ''}`.toLowerCase()).filter(Boolean)));
		return unique.sort();
	}

	escapeCsvField = (value) => {
		const text = `${value ?? ''}`;
		if (text.includes('"') || text.includes(',') || text.includes('\n')) {
			return `"${text.replace(/"/g, '""')}"`;
		}
		return text;
	}

	handleExportLogsCsv = () => {
		const filteredLogs = this.getFilteredLogs();
		if (!filteredLogs.length) {
			return;
		}
		const headers = ['time', 'actor', 'event', 'type', 'action', 'creator', 'job', 'station_id', 'item_id', 'time_entry_id'];
		const rows = filteredLogs.map((entry) => ([
			this.formatLogTimestamp(entry.created),
			this.getLogActorName(entry) || '',
			this.formatLogDescription(entry),
			this.formatStatusLabel(entry.event_type || 'event'),
			entry.action || '',
			entry.creator || '',
			entry.job || '',
			entry.station_id || '',
			entry.part_id || '',
			entry.time_entry_id || ''
		]));
		const csvContent = [headers, ...rows]
			.map((row) => row.map(this.escapeCsvField).join(','))
			.join('\n');
		const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
		const url = URL.createObjectURL(blob);
		const dateStamp = new Date().toISOString().slice(0, 10);
		const fileNameBase = (this.state.logsJobName || 'job-logs').toLowerCase().replace(/[^a-z0-9]+/g, '-');
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = `${fileNameBase}-${dateStamp}.csv`;
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
		URL.revokeObjectURL(url);
	}

	addQueryableProperties = (model, keys) => {
		const queryable = {queryable: true, settable: true};
		keys.forEach((key) => {
			model.properties[key] = queryable;
		});
	}

	handleOpenLogs = async (job) => {
		const workflow = this.parseWorkflow(job.workflow);
		const jobId = job?.pkey || job?.id || null;
		const jobName = job.name || workflow?.job?.name || `Job ${jobId || ''}`.trim();
		const requestToken = this.logsRequestToken + 1;
		this.logsRequestToken = requestToken;
		this.setState({
			logsModalOpen: true,
			logsLoading: true,
			logsError: '',
			logsEntries: [],
			logsJobId: jobId,
			logsJobName: jobName,
			logsFilterType: 'all',
			logsFilterText: '',
			logsFilterFrom: '',
			logsFilterTo: ''
		});

		try {
			const model = new API.BuildClass(Types.Mrp_Jobs._name, 'GET_LOGS');
			this.addQueryableProperties(model, ['job', 'pkey']);
			model.parent = this.getAccountParent();
			model.object.job = jobId;
			model.object.pkey = jobId;
			applyMrpLocationToRequest(model, this.props.rootState);
			const response = await model.submit(null, 'GET_LOGS');
			if (requestToken !== this.logsRequestToken) {
				return;
			}
			const rawItems = response?.items ?? response?.data ?? response ?? [];
			const logsEntries = Array.isArray(rawItems)
				? rawItems.filter((entry) => `${entry?.job ?? ''}` === `${jobId}`)
				: [];
			this.setState({logsEntries, logsLoading: false});
		} catch (error) {
			if (requestToken !== this.logsRequestToken) {
				return;
			}
			this.setState({
				logsLoading: false,
				logsError: 'Failed to load job logs.'
			});
		}
	}

	handleCloseLogs = () => {
		this.logsRequestToken += 1;
		this.setState({
			logsModalOpen: false,
			logsLoading: false,
			logsError: '',
			logsEntries: [],
			logsJobId: null,
			logsJobName: '',
			logsFilterType: 'all',
			logsFilterText: '',
			logsFilterFrom: '',
			logsFilterTo: ''
		});
	}

	// --- Render sections ---

	renderHeroBand() {
		const {jobs, stations, lots, models} = this.state;
		const activeJobs = jobs.filter(j => this.isJobActive(this.getJobStatus(j))).length;
		return (
			<div style={styles.heroBand} className="mb-4">
				<div className="row align-items-center">
					<div className="col-lg-8">
						<div style={styles.heroEyebrow}>ERP Dashboard</div>
						<h2 style={styles.heroTitle}>Review Jobs, Stations, Inventory, and Reports</h2>
						<div style={styles.heroSubtext}>
							Use ERP summaries to monitor active work, low-stock items, station coverage, and report totals for the selected location.
						</div>
					</div>
					<div className="col-lg-4 mt-3 mt-lg-0">
						<div style={styles.heroStatsWrap}>
							<div style={styles.heroStatRow}>
								<span style={styles.heroStatLabel}>Active Jobs</span>
								<strong style={styles.heroStatValue}>{activeJobs}</strong>
							</div>
							<div style={styles.heroStatRow}>
								<span style={styles.heroStatLabel}>Stations</span>
								<strong style={styles.heroStatValue}>{stations.length}</strong>
							</div>
							<div style={styles.heroStatRow}>
								<span style={styles.heroStatLabel}>Models / Lots</span>
								<strong style={styles.heroStatValue}>{models.length} / {lots.length}</strong>
							</div>
								<div className="mt-2">
									<MrpLocationControl
										rootState={this.props.rootState}
										id="mrp-dashboard-location"
										selectStyle={styles.heroSelect}
										note={LOCATION_NOTE}
									/>
								</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	renderSummaryCards() {
		const {jobs, stations, models, lots} = this.state;
		const activeJobs = jobs.filter(j => this.isJobActive(this.getJobStatus(j)));
		const lowStockParts = this.getLowStockParts();

		const cards = [
			{label: 'Total Jobs', value: jobs.length, icon: 'fa-tasks', color: '#114675', bg: 'linear-gradient(135deg, #e9f5ff 0%, #d6ebff 100%)'},
			{label: 'Active Jobs', value: activeJobs.length, icon: 'fa-play-circle', color: '#0a7f57', bg: 'linear-gradient(135deg, #e6fff4 0%, #d0f6e8 100%)'},
			{label: 'Stations', value: stations.length, icon: 'fa-cogs', color: '#6a3cc6', bg: 'linear-gradient(135deg, #f1eaff 0%, #e4d8ff 100%)', onClick: () => this.props.history.push('/erp/stations'), interactive: true, hint: 'Click to review stations'},
			{label: 'Models', value: models.length, icon: 'fa-cube', color: '#7a5322', bg: 'linear-gradient(135deg, #fff1dd 0%, #ffe7c5 100%)'},
			{label: 'Lots', value: lots.length, icon: 'fa-tags', color: '#5c5f69', bg: 'linear-gradient(135deg, #f5f6f8 0%, #eceff4 100%)'},
			{label: 'Low Stock Items', value: lowStockParts.length, icon: 'fa-exclamation-triangle', color: lowStockParts.length > 0 ? '#b72f20' : '#5c5f69', bg: lowStockParts.length > 0 ? 'linear-gradient(135deg, #ffeceb 0%, #ffd7d4 100%)' : 'linear-gradient(135deg, #f5f6f8 0%, #eceff4 100%)', onClick: this.handleOpenLowStockModal, interactive: lowStockParts.length > 0},
		];

		return (
			<div className="row mb-4">
				{cards.map((card, i) => (
					<div className="col-xl-2 col-lg-4 col-sm-6 mb-3" key={i}>
						<Card
							style={{
								...styles.summaryCardShell,
								...(card.interactive ? styles.summaryCardShellInteractive : null),
								background: card.bg
							}}
							onClick={card.onClick}
							role={card.interactive ? 'button' : undefined}
							tabIndex={card.interactive ? 0 : undefined}
							onKeyDown={card.interactive ? (event) => {
								if (event.key === 'Enter' || event.key === ' ') {
									event.preventDefault();
									card.onClick();
								}
							} : undefined}
						>
							<CardContent style={styles.summaryCard}>
								<i className={`fa ${card.icon}`} style={{fontSize: '24px', color: card.color, marginBottom: '8px', display: 'block'}}/>
								<div style={styles.summaryValue}>{card.value}</div>
								<div style={styles.summaryLabel}>{card.label}</div>
								{card.interactive && (
									<div style={styles.summaryCardHint}>{card.hint || 'Click to review items'}</div>
								)}
							</CardContent>
						</Card>
					</div>
				))}
			</div>
		);
	}

	renderJobStatusChart() {
		const {jobs} = this.state;
		const statusCounts = {};
		jobs.forEach(job => {
			const status = this.formatStatusLabel(this.getJobStatus(job));
			statusCounts[status] = (statusCounts[status] || 0) + 1;
		});
		const chartData = Object.entries(statusCounts).map(([name, count]) => [name, count]);
		const chartColors = ["#1bbc9b", "#67809f", "#c8d046", "#d64635", "#95a5a6", "#6a0dad"];

		return (
			<Card style={styles.panelCard}>
				<CardContent>
					<h6 style={styles.sectionTitle}>Jobs by Status</h6>
					{chartData.length > 0 ? (
						<PieChart colors={chartColors} data={chartData} legend="bottom"/>
					) : (
						<div style={styles.emptyState}>No jobs created yet</div>
					)}
				</CardContent>
			</Card>
		);
	}

	renderActiveJobs() {
		const {jobs} = this.state;
		const activeJobs = jobs.filter(j => this.isJobActive(this.getJobStatus(j)));
		const isScrollable = activeJobs.length > 3;

		return (
			<Card style={styles.panelCard}>
				<CardContent>
					<div className="d-flex justify-content-between align-items-center mb-3">
						<h6 style={{...styles.sectionTitle, marginBottom: 0}}>Active Jobs</h6>
						<Button
							size="sm"
							outline
							color="primary"
							onClick={() => this.props.history.push('/erp/job-tracker')}
						>
							View Tracker
						</Button>
					</div>
					{activeJobs.length === 0 ? (
						<div style={styles.emptyState}>No active jobs</div>
					) : (
						<div>
							{isScrollable && (
								<div style={styles.activeJobsOverflowNotice}>
									Showing 3 jobs at a time. Scroll to view the rest.
								</div>
							)}
							<div style={isScrollable ? styles.activeJobsListScrollable : styles.activeJobsList}>
								{activeJobs.map((job) => {
									const workflow = this.parseWorkflow(job.workflow);
									const status = this.getJobStatus(job);
									const progress = this.getJobProgress(job);
									const timeTotals = this.getJobTimeTotals(job);
								const showTimers = timeTotals.totalSeconds > 0 || timeTotals.laborActive || timeTotals.machineActive;
									const name = job.name || workflow?.job?.name || `Job ${job.pkey}`;
									const modelName = workflow?.model?.name || '';
									return (
										<div key={job.pkey} style={styles.jobRow}>
											<div className="d-flex justify-content-between align-items-center mb-1">
												<div>
													<strong style={{fontSize: '14px'}}>{name}</strong>
													{modelName && (
														<span className="text-muted ml-2" style={{fontSize: '12px'}}>{modelName}</span>
													)}
												</div>
												<Badge color={this.getStatusColor(status)} pill>
													{this.formatStatusLabel(status)}
												</Badge>
											</div>
											<div className="d-flex align-items-center">
												<div className="flex-grow-1">
													<Progress
														value={progress.percent}
														style={{height: '6px'}}
														color={progress.percent === 100 ? 'success' : 'primary'}
													/>
												</div>
												<span style={styles.progressLabel}>
													{progress.completedStations}/{progress.totalStations} stations
												</span>
											</div>
										{showTimers && (
											<div style={styles.jobTimerRow}>
												{this.renderJobTimerMetric('Labor', this.formatElapsed(timeTotals.laborSeconds), timeTotals.laborActive, 'primary')}
												{this.renderJobTimerMetric('Machine', this.formatElapsed(timeTotals.machineSeconds), timeTotals.machineActive, 'info')}
											</div>
										)}
											<div className="mt-2 text-right">
												<Button
													size="sm"
													outline
													color="secondary"
													onClick={() => this.handleOpenLogs(job)}
												>
													View Logs
												</Button>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					)}
				</CardContent>
			</Card>
		);
	}

	renderLogsModal() {
		const {logsModalOpen, logsLoading, logsError, logsEntries, logsJobName, logsFilterType, logsFilterText, logsFilterFrom, logsFilterTo} = this.state;
		const filteredLogs = this.getFilteredLogs();
		const logTypeOptions = this.getLogTypeOptions();
		return (
			<Modal
				isOpen={logsModalOpen}
				toggle={this.handleCloseLogs}
				size="xl"
				style={{maxWidth: '86vw', width: '86vw'}}
				>
					<ModalHeader toggle={this.handleCloseLogs}>
						Job Logs{logsJobName ? `: ${logsJobName}` : ''}
					</ModalHeader>
					<ModalBody>
						<div className="small text-muted mb-3">
							Logs include workflow changes, timer actions, manual time, scrap activity, and time-entry edits for this job. Filters and CSV export only use the rows loaded in this window.
						</div>
						{logsLoading && (
							<div className="text-center py-3">
							<Framework.Elements.LoadingSpinner isLoading={true}/>
						</div>
					)}
					{!logsLoading && logsError && (
						<div className="alert alert-danger mb-0">{logsError}</div>
					)}
					{!logsLoading && !logsError && logsEntries.length === 0 && (
						<div className="text-muted">No logs found for this job yet.</div>
					)}
					{!logsLoading && !logsError && logsEntries.length > 0 && (
						<div>
							<div className="d-flex align-items-end mb-3" style={{gap: '8px', flexWrap: 'wrap'}}>
								<div>
									<label className="small text-muted mb-1 d-block">Type</label>
									<select
										className="form-control form-control-sm"
										value={logsFilterType}
										onChange={(event) => this.setState({logsFilterType: event.target.value})}
									>
										<option value="all">All Types</option>
										{logTypeOptions.map((type) => (
											<option key={type} value={type}>{this.formatStatusLabel(type)}</option>
										))}
									</select>
								</div>
								<div style={{minWidth: '220px', flex: '1 1 220px'}}>
									<label className="small text-muted mb-1 d-block">Search</label>
									<input
										type="text"
										className="form-control form-control-sm"
										value={logsFilterText}
										onChange={(event) => this.setState({logsFilterText: event.target.value})}
										placeholder="Search event text..."
									/>
								</div>
								<div>
									<label className="small text-muted mb-1 d-block">From</label>
									<input
										type="date"
										className="form-control form-control-sm"
										value={logsFilterFrom}
										onChange={(event) => this.setState({logsFilterFrom: event.target.value})}
									/>
								</div>
								<div>
									<label className="small text-muted mb-1 d-block">To</label>
									<input
										type="date"
										className="form-control form-control-sm"
										value={logsFilterTo}
										onChange={(event) => this.setState({logsFilterTo: event.target.value})}
									/>
								</div>
								<div>
									<Button
										size="sm"
										outline
										color="secondary"
										onClick={() => this.setState({logsFilterType: 'all', logsFilterText: '', logsFilterFrom: '', logsFilterTo: ''})}
									>
										Clear
									</Button>
								</div>
								<div>
									<Button
										size="sm"
										color="primary"
										onClick={this.handleExportLogsCsv}
										disabled={filteredLogs.length === 0}
									>
										Export CSV
									</Button>
								</div>
								<div className="ml-auto small text-muted">
									Showing {filteredLogs.length} of {logsEntries.length}
								</div>
							</div>
							<div className="table-responsive">
								<table className="table table-sm mb-0">
									<thead>
									<tr>
										<th style={{width: '190px'}}>Time</th>
										<th style={{width: '180px'}}>Actor</th>
										<th>Event</th>
										<th style={{width: '160px'}}>Type</th>
									</tr>
								</thead>
								<tbody>
									{filteredLogs.map((entry) => (
										<tr key={entry.pkey || `${entry.created}-${entry.action}`}>
											<td>{this.formatLogTimestamp(entry.created)}</td>
											<td>{this.getLogActorName(entry) || '-'}</td>
											<td>{this.formatLogDescription(entry)}</td>
											<td>{this.formatStatusLabel(entry.event_type || 'event')}</td>
										</tr>
									))}
									</tbody>
								</table>
							</div>
							{filteredLogs.length === 0 && (
								<div className="text-muted mt-3">No logs match the current filters.</div>
							)}
						</div>
					)}
				</ModalBody>
			</Modal>
		);
	}

	renderReportOverviewCard = ({title, description, metrics, reportKey}) => {
		return (
			<Card style={styles.reportOverviewCard}>
				<CardContent style={{padding: '14px'}}>
					<h6 style={{...styles.sectionTitle, marginBottom: '6px'}}>{title}</h6>
					<div className="text-muted mb-2" style={{fontSize: '13px'}}>{description}</div>
					<div className="d-flex flex-column mb-3" style={{gap: '6px'}}>
						{metrics.map((metric) => (
							<div key={metric.label} style={styles.reportMetricRow}>
								<span>{metric.label}</span>
								<strong>{metric.value}</strong>
							</div>
						))}
					</div>
					<div className="text-right">
						<Button
							size="sm"
							outline
							color="primary"
							onClick={() => this.props.history.push(`/erp/reports?report=${reportKey}`)}
						>
							View Full Report
						</Button>
					</div>
				</CardContent>
			</Card>
		);
	}

	renderReportsSection() {
		const {reportSummary, reportsLoading, reportsError, reportDays} = this.state;
		const dayOptions = [7, 30, 90];

		return (
			<div className="row mt-4">
				<div className="col-12">
					<Card style={styles.panelCard}>
						<CardContent>
							<div className="d-flex justify-content-between align-items-center mb-3" style={{gap: '8px', flexWrap: 'wrap'}}>
								<div>
									<h6 style={{...styles.sectionTitle, marginBottom: '2px'}}>Reports Overview</h6>
									<div className="text-muted" style={{fontSize: '13px'}}>Quick operational snapshot. Open full reports for filtering and CSV exports.</div>
								</div>
								<div className="d-flex align-items-center" style={{gap: '8px'}}>
									<div className="btn-group btn-group-sm">
										{dayOptions.map((days) => (
											<Button
												key={days}
												size="sm"
												color={reportDays === days ? 'primary' : 'secondary'}
												outline={reportDays !== days}
												onClick={() => this.handleReportWindowChange(days)}
												disabled={reportsLoading}
											>
												{days}d
											</Button>
										))}
									</div>
									<Button
										size="sm"
										outline
										color="primary"
										onClick={this.handleRefreshReports}
										disabled={reportsLoading}
									>
										Refresh
									</Button>
									<Button
										size="sm"
										color="primary"
										onClick={() => this.props.history.push('/erp/reports')}
									>
										Open Reports Center
									</Button>
								</div>
							</div>

							{reportsError && (
								<div className="alert alert-warning py-2">{reportsError}</div>
							)}

							{reportsLoading ? (
								<div className="text-center py-3">
									<Framework.Elements.LoadingSpinner isLoading={true}/>
								</div>
							) : (
								<div className="row">
									<div className="col-lg-6 mb-3">
										{this.renderReportOverviewCard({
											title: 'Parts Inventory',
											description: 'Current on-hand and low-stock exposure.',
											reportKey: 'parts_inventory',
											metrics: [
												{label: 'Inventory Value', value: this.formatCurrency(reportSummary?.parts_inventory?.inventory_value_total)},
												{label: 'Low Stock Items', value: this.formatNumber(reportSummary?.parts_inventory?.below_alert_count, 0)},
												{label: 'Rows', value: this.formatNumber(reportSummary?.parts_inventory?.rows, 0)}
											]
										})}
									</div>

									<div className="col-lg-6 mb-3">
										{this.renderReportOverviewCard({
											title: 'Guns Built / Throughput',
											description: 'Production output and time efficiency.',
											reportKey: 'guns_built',
											metrics: [
												{label: 'Units Built', value: this.formatNumber(reportSummary?.guns_built?.units_reported_total, 4)},
												{label: 'Labor Hours', value: this.formatNumber(reportSummary?.guns_built?.labor_hours_total)},
												{label: 'Machine Hours', value: this.formatNumber(reportSummary?.guns_built?.machine_hours_total)}
											]
										})}
									</div>

									<div className="col-lg-6 mb-3">
										{this.renderReportOverviewCard({
											title: 'Time / Labor',
											description: 'Labor and machine time utilization.',
											reportKey: 'labor_time',
											metrics: [
												{label: 'Total Hours', value: this.formatNumber(reportSummary?.labor_time?.total_hours)},
												{label: 'Manual Entries', value: this.formatNumber(reportSummary?.labor_time?.manual_entry_count, 0)},
												{label: 'Rows', value: this.formatNumber(reportSummary?.labor_time?.rows, 0)}
											]
										})}
									</div>

									<div className="col-lg-6 mb-3">
										{this.renderReportOverviewCard({
											title: 'Waste / Scrap',
											description: 'Scrap quantity and cost trend.',
											reportKey: 'scrap',
											metrics: [
												{label: 'Scrap Cost', value: this.formatCurrency(reportSummary?.scrap?.scrap_cost_total)},
												{label: 'Scrap Qty', value: this.formatNumber(reportSummary?.scrap?.quantity_scrapped_total, 4)},
												{label: 'Rows', value: this.formatNumber(reportSummary?.scrap?.rows, 0)}
											]
										})}
									</div>
								</div>
							)}
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	renderLowStockAlerts() {
		const lowStockParts = this.getLowStockParts();
		if (lowStockParts.length === 0) return null;

		return (
			<div className="row mt-4">
				<div className="col-12">
					<Card style={styles.panelCard}>
						<CardContent>
							<h6 style={{...styles.sectionTitle, color: '#d64635'}}>
								<i className="fa fa-exclamation-triangle mr-2"/>
								Low Stock Alerts
							</h6>
							<div className="table-responsive">
								<table className="table table-sm mb-0">
									<thead>
										<tr>
											<th>Item</th>
											<th>Station</th>
											<th className="text-right">On Hand</th>
											<th className="text-right">Alert Level</th>
											<th style={{width: '200px'}}>Stock Level</th>
										</tr>
									</thead>
									<tbody>
										{lowStockParts.map((part) => {
											const qty = Number(part.quantity) || 0;
											const alert = Number(part.soft_alert ?? part.alert_quantity) || 0;
											const ratio = alert > 0 ? qty / alert : 1;
											return (
												<tr key={part.pkey}>
													<td><strong>{part.name}</strong></td>
													<td>{this.getStationName(part.station)}</td>
													<td className="text-right">{qty}</td>
													<td className="text-right">{alert}</td>
													<td>
														<Progress
															value={qty}
															max={alert}
															color={this.getProgressColor(ratio)}
															style={{height: '6px'}}
														/>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	renderLowStockModal() {
		const {lowStockModalOpen, lowStockFilterText} = this.state;
		const lowStockParts = this.getLowStockParts();
		const filteredLowStockParts = this.getFilteredLowStockParts();

		return (
			<Modal
				isOpen={lowStockModalOpen}
				toggle={this.handleCloseLowStockModal}
				size="lg"
			>
				<ModalHeader toggle={this.handleCloseLowStockModal}>
					Low Stock Items
				</ModalHeader>
				<ModalBody>
					<div className="small text-muted mb-3">
						Review low-stock items for the active ERP location and jump straight to each vendor reorder page when a reorder URL is configured.
					</div>
					{lowStockParts.length > 0 && (
						<div className="d-flex align-items-end justify-content-between mb-3" style={{gap: '10px', flexWrap: 'wrap'}}>
							<div style={{minWidth: '240px', flex: '1 1 240px'}}>
								<label className="small text-muted mb-1 d-block">Filter</label>
								<input
									type="text"
									className="form-control form-control-sm"
									value={lowStockFilterText}
									onChange={(event) => this.setState({lowStockFilterText: event.target.value})}
									placeholder="Search item name, SKU, UPC, or station..."
								/>
							</div>
							<div className="small text-muted" style={{paddingBottom: '6px'}}>
								Showing {filteredLowStockParts.length} of {lowStockParts.length}, sorted by urgency.
							</div>
						</div>
					)}
					{lowStockParts.length === 0 ? (
						<div className="text-muted mb-0">No low-stock items for the current location.</div>
					) : (
						<div className="table-responsive">
							<table className="table table-sm mb-0">
								<thead>
									<tr>
										<th>Item</th>
										<th>Station</th>
										<th className="text-right">On Hand</th>
										<th className="text-right">Alert Level</th>
										<th style={{width: '140px'}}>Reorder</th>
									</tr>
								</thead>
								<tbody>
									{filteredLowStockParts.map((part) => {
										const qty = Number(part.quantity) || 0;
										const alert = Number(part.soft_alert ?? part.alert_quantity) || 0;
										const normalizedOrderUrl = this.normalizeOrderUrl(part.order_url);
										const hasOrderUrl = !!normalizedOrderUrl;
										return (
											<tr key={part.pkey}>
												<td>
													<strong>{part.name || `Item ${part.pkey}`}</strong>
													{part.sku && (
														<div className="text-muted" style={{fontSize: '12px'}}>SKU: {part.sku}</div>
													)}
												</td>
												<td>{this.getStationName(part.station)}</td>
												<td className="text-right">{this.formatNumber(qty, 4)}</td>
												<td className="text-right">{this.formatNumber(alert, 4)}</td>
												<td>
													{hasOrderUrl ? (
														<Button
															tag="a"
															href={normalizedOrderUrl}
															target="_blank"
															rel="noopener noreferrer"
															color="primary"
															size="sm"
														>
															Reorder
														</Button>
													) : (
														<Button
															color="secondary"
															size="sm"
															disabled
														>
															No URL
														</Button>
													)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
							{filteredLowStockParts.length === 0 && (
								<div className="text-muted mt-3">No low-stock items match the current filter.</div>
							)}
						</div>
					)}
				</ModalBody>
			</Modal>
		);
	}

	renderStationsOverview() {
		const {stations, parts, jobs} = this.state;
		if (stations.length === 0) return null;

		const activeJobs = jobs.filter(j => this.isJobActive(this.getJobStatus(j)));

		return (
			<div className="row mt-4">
				<div className="col-12">
					<Card style={styles.panelCard}>
						<CardContent>
							<div className="d-flex justify-content-between align-items-center mb-3">
								<h6 style={{...styles.sectionTitle, marginBottom: 0}}>Stations</h6>
								<Button
									size="sm"
									outline
									color="primary"
									onClick={() => this.props.history.push('/erp/stations')}
								>
									Manage Stations
								</Button>
							</div>
							<div className="row">
								{stations.map((station) => {
									const stationParts = parts.filter(p => `${p.station}` === `${station.pkey}`);
									const lowParts = stationParts.filter(p => {
										const qty = Number(p.quantity) || 0;
										const alert = Number(p.soft_alert ?? p.alert_quantity) || 0;
										return alert > 0 && qty <= alert;
									});

									// Count active jobs referencing this station in their workflow
									const activeJobCount = activeJobs.filter(job => {
										const workflow = this.parseWorkflow(job.workflow);
										if (!workflow?.stations) return false;
										return workflow.stations.some(s =>
											`${s.station_id}` === `${station.pkey}` &&
											!['complete', 'completed', 'done'].includes(`${s.status || ''}`.toLowerCase())
										);
									}).length;

									return (
										<div className="col-md-4 col-lg-3 mb-3" key={station.pkey}>
											<div style={styles.stationCard}>
												<div style={styles.stationName}>
													<i className="fa fa-cog mr-2" style={{color: '#67809f'}}/>
													{station.name}
												</div>
												<div style={styles.stationStat}>
													<span>Items</span>
													<strong>{stationParts.length}</strong>
												</div>
												{lowParts.length > 0 && (
													<div style={{...styles.stationStat, color: '#d64635'}}>
														<span>Low Stock</span>
														<strong>{lowParts.length}</strong>
													</div>
												)}
												<div style={styles.stationStat}>
													<span>Active Jobs</span>
													<strong style={{color: activeJobCount > 0 ? '#1bbc9b' : '#999'}}>
														{activeJobCount}
													</strong>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	renderQuickActions() {
		const actions = [
			{label: 'Create Job', icon: 'fa-plus', route: '/erp/jobs', style: styles.quickActionPrimary},
			{label: 'Job Tracker', icon: 'fa-tasks', route: '/erp/job-tracker', style: styles.quickActionSecondary},
			{label: 'Stations', icon: 'fa-cogs', route: '/erp/stations', style: styles.quickActionSecondary},
			{label: 'Station Work', icon: 'fa-wrench', route: '/erp/work', style: styles.quickActionSecondary},
			{label: 'Reports', icon: 'fa-file-text-o', route: '/erp/reports', style: styles.quickActionSecondary},
		];

		return (
			<div className="row mb-4">
				<div className="col-12">
					<div style={styles.quickActionsWrap}>
						{actions.map((action) => (
							<Button
								key={action.label}
								color={action.label === 'Create Job' ? 'primary' : 'secondary'}
								outline={action.label !== 'Create Job'}
								style={action.style}
								onClick={() => this.props.history.push(action.route)}
							>
								<i className={`fa ${action.icon}`} style={styles.quickActionIcon}/>
								<span>{action.label}</span>
							</Button>
						))}
					</div>
				</div>
			</div>
		);
	}

	render() {
		if (this.state.loading) {
			return (
				<div className="container-fluid">
					<Framework.Components.PageBar breadcrumbs={breadcrumbsStatic} history={this.props.history}/>
					<Framework.Elements.LoadingSpinner isLoading={true}/>
				</div>
			);
		}

		return (
			<div className="container-fluid">
				<Framework.Components.PageBar breadcrumbs={breadcrumbsStatic} history={this.props.history}/>

				{this.renderHeroBand()}
				{this.renderQuickActions()}
				{this.renderSummaryCards()}

				<div className="row">
					<div className="col-md-4">
						{this.renderJobStatusChart()}
					</div>
					<div className="col-md-8">
						{this.renderActiveJobs()}
					</div>
				</div>
				{this.renderReportsSection()}

					{this.renderLowStockAlerts()}
					{this.renderStationsOverview()}
					{this.renderLowStockModal()}
					{this.renderLogsModal()}
				</div>
			);
		}
	}

const styles = {
	heroBand: {
		background: 'linear-gradient(135deg, #eef7ff 0%, #f9fcff 42%, #e4f2fb 100%)',
		border: '1px solid #d6e2f0',
		borderTop: '4px solid #2b7dbc',
		borderRadius: '14px',
		padding: '24px',
		color: '#17324d',
		boxShadow: '0 10px 24px rgba(12, 55, 92, 0.08)',
	},
	heroEyebrow: {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '4px 10px',
		borderRadius: '999px',
		backgroundColor: '#dceeff',
		color: '#185f95',
		fontSize: '11px',
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: '0.08em',
	},
	heroTitle: {
		fontSize: '27px',
		fontWeight: 700,
		lineHeight: 1.2,
		margin: '8px 0 10px',
	},
	heroSubtext: {
		fontSize: '14px',
		color: '#557086',
		maxWidth: '540px',
	},
	heroStatsWrap: {
		background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, #edf6ff 100%)',
		border: '1px solid #d7e4f2',
		borderRadius: '12px',
		padding: '12px 14px',
		boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.75)',
	},
	heroSelect: {
		backgroundColor: '#ffffff',
		border: '1px solid #c9d9e8',
		color: '#17324d',
	},
	heroStatRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '7px 0',
		fontSize: '13px',
		borderBottom: '1px solid #e3ecf5',
		gap: '8px',
	},
	heroStatLabel: {
		color: '#5f7489',
	},
	heroStatValue: {
		color: '#17324d',
		fontSize: '15px',
		fontWeight: 700,
	},
	quickActionPrimary: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '10px',
		minHeight: '68px',
		padding: '14px 24px',
		minWidth: '190px',
		fontSize: '16px',
		fontWeight: 700,
		borderRadius: '14px',
		boxShadow: '0 8px 16px rgba(14, 77, 132, 0.25)',
	},
	quickActionSecondary: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '10px',
		minHeight: '68px',
		padding: '14px 22px',
		minWidth: '190px',
		fontSize: '16px',
		fontWeight: 600,
		borderRadius: '14px',
		borderColor: '#9db7d4',
		color: '#285077',
		backgroundColor: '#f5faff',
		boxShadow: '0 6px 12px rgba(32, 81, 119, 0.08)',
	},
	quickActionsWrap: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '12px',
		alignItems: 'stretch',
	},
	quickActionIcon: {
		fontSize: '18px',
	},
	summaryCardShell: {
		borderRadius: '12px',
		boxShadow: '0 10px 20px rgba(14, 44, 80, 0.08)',
		border: '1px solid rgba(17, 70, 117, 0.08)',
		height: '100%',
	},
	summaryCardShellInteractive: {
		cursor: 'pointer',
	},
	summaryCard: {
		padding: '16px',
		textAlign: 'center',
	},
	summaryValue: {
		fontSize: '28px',
		fontWeight: '700',
		color: '#333',
		lineHeight: '1.2',
	},
	summaryLabel: {
		fontSize: '13px',
		color: '#888',
		fontWeight: '500',
		marginTop: '2px',
	},
	summaryCardHint: {
		fontSize: '11px',
		fontWeight: 600,
		color: '#7c4a43',
		marginTop: '8px',
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
	},
	sectionTitle: {
		fontWeight: '600',
		marginBottom: '16px',
		fontSize: '15px',
		color: '#333',
	},
	panelCard: {
		height: '100%',
		borderRadius: '12px',
		border: '1px solid #d6e1ef',
		boxShadow: '0 8px 18px rgba(15, 56, 99, 0.08)',
	},
	reportOverviewCard: {
		height: '100%',
		borderRadius: '10px',
		border: '1px solid #e1eaf5',
		background: '#fdfefe',
		boxShadow: '0 6px 14px rgba(12, 46, 82, 0.06)',
	},
	reportMetricRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		fontSize: '13px',
		color: '#466684',
		padding: '6px 8px',
		borderRadius: '8px',
		backgroundColor: '#f3f8fe',
		border: '1px solid #e0edf9',
	},
	emptyState: {
		color: '#999',
		textAlign: 'center',
		padding: '40px 0',
		fontSize: '14px',
	},
	activeJobsList: {
		display: 'flex',
		flexDirection: 'column',
	},
	activeJobsListScrollable: {
		display: 'flex',
		flexDirection: 'column',
		maxHeight: '470px',
		overflowY: 'auto',
		paddingRight: '8px',
		marginRight: '-8px',
		scrollbarGutter: 'stable',
	},
	activeJobsOverflowNotice: {
		fontSize: '12px',
		color: '#5f7489',
		marginBottom: '10px',
	},
	jobRow: {
		marginBottom: '14px',
		paddingBottom: '14px',
		borderBottom: '1px solid #edf3fa',
	},
	progressLabel: {
		marginLeft: '12px',
		color: '#888',
		fontSize: '12px',
		minWidth: '80px',
		textAlign: 'right',
		whiteSpace: 'nowrap',
	},
	jobTimerRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '8px',
		marginTop: '10px',
	},
	jobTimerMetric: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '6px',
		padding: '6px 10px',
		borderRadius: '999px',
		backgroundColor: '#f4f8fc',
		border: '1px solid #dbe6f2',
		fontSize: '12px',
		color: '#466684',
	},
	jobTimerMetricLabel: {
		fontWeight: 700,
		color: '#17324d',
	},
	jobTimerMetricValue: {
		fontVariantNumeric: 'tabular-nums',
	},
	jobTimerBadge: {
		fontSize: '10px',
		letterSpacing: '0.03em',
	},
	stationCard: {
		border: '1px solid #dce8f6',
		borderRadius: '10px',
		padding: '16px',
		background: 'linear-gradient(145deg, #ffffff 0%, #f6faff 100%)',
		height: '100%',
	},
	stationName: {
		fontWeight: '600',
		fontSize: '15px',
		marginBottom: '10px',
		color: '#333',
	},
	stationStat: {
		display: 'flex',
		justifyContent: 'space-between',
		fontSize: '13px',
		color: '#666',
		marginBottom: '4px',
	},
};

export default Framework.Components.withRootState(MrpDashboard);
